/**
 * Wishlist / favorites: toggles customer metafields via the Paint Factory wishlist app.
 *
 * Requests hit the app host directly (WISHLIST_APP_HOST), passing the shop's
 * .myshopify.com domain as a `?shop=` query parameter (window.themeSettings.shopDomain).
 */

/**
 * @typedef {{
 *   shopDomain?: string,
 *   routes?: { account_login_url?: string }
 * }} ThemeSettings
 */

/**
 * @returns {ThemeSettings | undefined}
 */
function getThemeSettings() {
  return /** @type {Window & { themeSettings?: ThemeSettings }} */ (window).themeSettings;
}

const METAFIELD_NAMESPACE = 'custom';

const WISHLIST_APP_HOST = 'https://paint-factory.apps.esign.eu';

/**
 * Builds the wishlist endpoint by hitting the app host directly.
 * @param {string} customerId
 * @returns {string} Full request URL.
 */
function buildWishlistUrl(customerId) {
  const settings = getThemeSettings();
  const shopDomain = settings?.shopDomain ?? '';
  const shopParam = shopDomain ? `?shop=${encodeURIComponent(shopDomain)}` : '';
  return `${WISHLIST_APP_HOST}/customers/${customerId}/wishlist${shopParam}`;
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} loading
 */
function setLoading(button, loading) {
  button.toggleAttribute('aria-busy', loading);
  button.disabled = loading;
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} favorited
 */
function applyFavoriteState(button, favorited) {
  button.dataset.isFavorited = favorited ? 'true' : 'false';
  button.setAttribute('aria-pressed', favorited ? 'true' : 'false');
  button.classList.toggle('wishlist-button--active', favorited);
}

const FAVORITES_ROW_SELECTOR = 'li.product-grid__item, .resource-list__item';

/**
 * @param {HTMLButtonElement} button
 */
function removeFavoritesPageRow(button) {
  const row = button.closest(FAVORITES_ROW_SELECTOR);
  if (row?.parentElement) {
    row.remove();
  } else {
    window.location.reload();
    return;
  }
  showWishlistEmptyStateIfNoItems(button);
}

/**
 * When the last favorite is removed on the wishlist page, show the empty message (no full reload).
 * @param {HTMLButtonElement} button
 */
function showWishlistEmptyStateIfNoItems(button) {
  const section = button.closest('[data-wishlist-favorites-section]');
  if (!section) return;
  const gridRegion = section.querySelector('[data-wishlist-favorites-grid-region]');
  const emptyEl = section.querySelector('[data-wishlist-favorites-empty]');
  if (!gridRegion || !emptyEl) return;
  const items = section.querySelectorAll(FAVORITES_ROW_SELECTOR);
  if (items.length > 0) return;
  /** @type {HTMLElement} */ (gridRegion).hidden = true;
  /** @type {HTMLElement} */ (emptyEl).hidden = false;
}

/**
 * @param {number} delta
 */
function updateWishlistHeaderCount(delta) {
  const bubbles = document.querySelectorAll('[data-wishlist-count]');
  bubbles.forEach((bubble) => {
    if (!(bubble instanceof HTMLElement)) return;
    let count = parseInt(bubble.dataset.wishlistCount || '0', 10);
    count = Math.max(0, count + delta);
    bubble.dataset.wishlistCount = String(count);
    const countEl = bubble.querySelector('.wishlist-bubble__count');
    if (countEl) countEl.textContent = String(count);
    bubble.classList.toggle('visually-hidden', count === 0);
    const icon = bubble.closest('.wishlist-icon');
    if (icon) icon.classList.toggle('wishlist-icon--has-items', count > 0);
  });
}

/**
 * @param {HTMLButtonElement} button
 */
function showLoginTooltip(button) {
  const tooltipId = button.dataset.wishlistLoginTooltip;
  if (!tooltipId) return;
  const tooltip = /** @type {HTMLElement | null} */ (document.getElementById(tooltipId));
  if (!tooltip || typeof tooltip.showPopover !== 'function') return;

  // Move to body to escape any transformed ancestor (which breaks position:fixed)
  if (tooltip.parentElement !== document.body) {
    document.body.appendChild(tooltip);
  }
  tooltip.showPopover();
  const rect = button.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  tooltip.style.top = `${rect.top - tooltipRect.height - 8}px`;
  tooltip.style.left = `${rect.left + rect.width / 2 - tooltipRect.width / 2}px`;
  tooltip.style.transform = 'none';

  const autoClose = setTimeout(() => {
    if (tooltip.matches(':popover-open')) tooltip.hidePopover();
  }, 3000);

  const onScroll = () => {
    if (tooltip.matches(':popover-open')) tooltip.hidePopover();
    clearTimeout(autoClose);
    window.removeEventListener('scroll', onScroll, true);
  };
  window.addEventListener('scroll', onScroll, true);
}

document.addEventListener('click', async (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const el = target.closest('button[data-wishlist-trigger]');
  if (!(el instanceof HTMLButtonElement)) return;
  const button = el;

  event.preventDefault();

  const customerId = button.dataset.customerId;
  if (!customerId) {
    showLoginTooltip(button);
    return;
  }

  const url = buildWishlistUrl(customerId);
  if (!url) {
    console.warn('[wishlist] Missing wishlist app base — configure the staging URL or app proxy in theme settings.');
    return;
  }

  const productId = button.dataset.productId;
  const metafieldKey = button.dataset.metafieldKey || 'wishlist_products';
  if (!productId) {
    console.error('[wishlist] Missing data-product-id on button');
    return;
  }

  const wasFavorited = button.dataset.isFavorited === 'true';
  const method = wasFavorited ? 'DELETE' : 'POST';

  const body = JSON.stringify({
    metafield_namespace: METAFIELD_NAMESPACE,
    metafield_key: metafieldKey,
    metafield_value: productId,
  });

  setLoading(button, true);

  try {
    const response = await fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const nowFavorited = !wasFavorited;
    applyFavoriteState(button, nowFavorited);
    updateWishlistHeaderCount(nowFavorited ? 1 : -1);

    if (button.dataset.wishlistContext === 'favorites-page' && !nowFavorited) {
      removeFavoritesPageRow(button);
    }
  } catch (error) {
    console.error('[wishlist] API error:', error);
  } finally {
    setLoading(button, false);
  }
});
